import { add, differenceInDays, isAfter, isBefore, format } from 'date-fns';
import { getStoredSubTask } from 'platform/forms/sub-task';
import {
  parseDate,
  parseDateWithTemplate,
  DATE_TEMPLATE,
} from '../../shared/utils/dates';
import { GET_STARTED_PATH } from '../constants';
import { trackScreenerReenteredFromForm } from '../utils/tracking/features/screenerTracking';

// Helper to get current date as Date object
export const maxDate = format(add(new Date(), { years: 100 }), DATE_TEMPLATE);

export const getDate = date => parseDateWithTemplate(date);
export const isDateComplete = date => date?.length === DATE_TEMPLATE.length;
export const isDateInFuture = date => isAfter(date, new Date());
export const isDateLessThanMax = date => {
  const maxDateObj = parseDate(maxDate);
  return isBefore(date, maxDateObj);
};

export const isValidDate = date => {
  if (date && isDateComplete(date)) {
    const dateObj = getDate(date);
    return isDateInFuture(dateObj) && isDateLessThanMax(dateObj);
  }
  return false;
};

/**
 * Page names a Veteran can reach with their stored answers, in order. Follows
 * each page's `next`; stops at the first unanswered page, or at the last page
 * whose destination leaves the sub-task (e.g. the URL into the form). A page's
 * `next` must be pure.
 */
const getReachablePageNames = (pages, data) => {
  const path = [];
  let page = pages[0];

  while (page && !path.includes(page.name)) {
    path.push(page.name);
    if (typeof page.validate === 'function' && !page.validate(data)) {
      break;
    }
    const destination =
      typeof page.next === 'function' ? page.next(data) : page.next;
    page = pages.find(({ name }) => name === destination);
  }

  return path;
};

/**
 * Page a Veteran resumes on: the furthest page their stored answers reach.
 * @param {Array} pages - sub-task page objects
 * @param {Object} data - stored sub-task data
 * @returns {String|undefined} page name
 */
export const getResumePageName = (pages, data = {}) => {
  const reachable = getReachablePageNames(pages, data);
  return reachable[reachable.length - 1];
};

/**
 * Stored screener answers mean the Veteran entered the form from the screener.
 * The introduction page clears them, so entering from there returns false.
 * @returns {Boolean}
 */
export const hasScreenerAnswers = () =>
  Boolean(getStoredSubTask()['view:militaryStatus']);

/**
 * Creates the onNavBack for the first form page. Stored screener answers send
 * Back to the screener, which reopens at the furthest page they answered;
 * otherwise the default previous page (/introduction) is used.
 * @param {String} formVersion - 'v1' or 'v2', reported with the tracking event
 */
export const createOnNavBackToScreener =
  formVersion =>
  ({ goPath, goPreviousPath }) => {
    if (hasScreenerAnswers()) {
      trackScreenerReenteredFromForm({ formVersion });
      goPath(GET_STARTED_PATH);
    } else {
      goPreviousPath();
    }
  };

export const getDiffInDays = date => {
  const dateDischarge = getDate(date);
  const dateToday = new Date();
  return differenceInDays(dateDischarge, dateToday);
};
